import React, { useState, useEffect, useCallback } from 'react';
import { Course, Track } from '../../types';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { usePageTitle } from '../../hooks/usePageTitle';
import { invalidateTracksCache } from '../../hooks/useTracks';
import { FALLBACK_TRACK_COLOR, FALLBACK_TRACK_ICON } from '../../data/defaultTracks';

const emptyFormData = {
  name: '',
  description: '',
  icon: '',
  color: FALLBACK_TRACK_COLOR,
  sortOrder: '',
};

type TrackFormData = typeof emptyFormData;

const ManageTracks: React.FC = () => {
  usePageTitle('Manage Tracks');
  const [tracks, setTracks] = useState<Track[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Track | null>(null);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState<TrackFormData>(emptyFormData);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const authFetch = useAuthFetch();

  const fetchData = useCallback(async () => {
    try {
      setError('');
      const [tracksRes, coursesRes] = await Promise.all([
        authFetch('/api/tracks'),
        authFetch('/api/courses'),
      ]);
      if (tracksRes.ok) {
        setTracks(await tracksRes.json());
      } else {
        const data = await tracksRes.json().catch(() => null);
        setError(data?.message || 'Failed to load tracks.');
      }
      if (coursesRes.ok) setCourses(await coursesRes.json());
    } catch (err) {
      console.error('Error fetching tracks:', err);
      setError('Failed to load tracks.');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const courseCount = (trackName: string) =>
    courses.filter((c) => c.track === trackName).length;

  const handleEdit = (track: Track) => {
    setEditing(track);
    setCreating(false);
    setFormData({
      name: track.name,
      description: track.description,
      icon: track.icon,
      color: track.color,
      sortOrder: String(track.sortOrder),
    });
    setError('');
  };

  const handleCreate = () => {
    setCreating(true);
    setEditing(null);
    const maxSortOrder = tracks.reduce((max, t) => Math.max(max, t.sortOrder), 0);
    setFormData({ ...emptyFormData, sortOrder: String(maxSortOrder + 1) });
    setError('');
  };

  const handleCancel = () => {
    setEditing(null);
    setCreating(false);
    setError('');
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Name is required.');
      return;
    }
    const sortOrder = Number(formData.sortOrder);
    if (formData.sortOrder.trim() !== '' && !Number.isFinite(sortOrder)) {
      setError('Sort order must be a number.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const url = editing ? `/api/manage/tracks/${editing.id}` : '/api/manage/tracks';
      const method = editing ? 'PUT' : 'POST';
      const payload: Record<string, unknown> = {
        name: formData.name.trim(),
        description: formData.description,
        icon: formData.icon.trim(),
        color: formData.color,
      };
      if (formData.sortOrder.trim() !== '') payload.sortOrder = sortOrder;

      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const data = await res.json().catch(() => null);
        if (editing && data?.coursesUpdated > 0) {
          alert(`Track renamed. ${data.coursesUpdated} course(s) were updated to the new track name.`);
        }
        invalidateTracksCache();
        await fetchData();
        handleCancel();
      } else {
        const data = await res.json().catch(() => null);
        setError(data?.message || 'Failed to save track.');
      }
    } catch {
      setError('An error occurred while saving.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (track: Track) => {
    const count = courseCount(track.name);
    if (count > 0) {
      alert(`Cannot delete '${track.name}' — ${count} course(s) are assigned to it. Reassign those courses to another track first.`);
      return;
    }
    if (!window.confirm(`Delete the track '${track.name}'? This cannot be undone.`)) return;
    try {
      const res = await authFetch(`/api/manage/tracks/${track.id}`, { method: 'DELETE' });
      if (res.ok) {
        invalidateTracksCache();
        fetchData();
      } else {
        const data = await res.json().catch(() => null);
        alert(data?.message || 'Failed to delete track.');
      }
    } catch (err) {
      console.error('Error deleting track:', err);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading tracks...</p>
      </div>
    );
  }

  const showForm = creating || editing !== null;

  return (
    <div className="admin-manage-page">
      <section className="dashboard-header">
        <div className="section-container">
          <div className="section-header-row">
            <div>
              <h1 className="page-title">Manage Learning Tracks</h1>
              <p className="page-subtitle">{tracks.length} track{tracks.length !== 1 ? 's' : ''}</p>
            </div>
            {!showForm && (
              <button className="btn btn-primary" onClick={handleCreate}>
                + Add Track
              </button>
            )}
          </div>
        </div>
      </section>

      {error && !showForm && (
        <section className="section">
          <div className="section-container">
            <div className="form-error">{error}</div>
          </div>
        </section>
      )}

      {showForm && (
        <section className="section">
          <div className="section-container">
            <div className="admin-form">
              <h2 className="section-title">{editing ? 'Edit Track' : 'New Track'}</h2>

              {error && <div className="form-error" style={{ marginBottom: '16px' }}>{error}</div>}

              <div className="form-grid">
                <div className="form-group form-full">
                  <label className="form-label">Name *</label>
                  <input
                    className="form-input"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Threat Protection & Security Operations"
                  />
                  {editing && courseCount(editing.name) > 0 && formData.name.trim() !== editing.name && (
                    <p style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginTop: '6px' }}>
                      Renaming will automatically update the {courseCount(editing.name)} course(s) assigned to this track.
                    </p>
                  )}
                </div>

                <div className="form-group form-full">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-textarea"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    placeholder="Shown on the homepage track card..."
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Icon (emoji)</label>
                  <input
                    className="form-input"
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    placeholder={FALLBACK_TRACK_ICON}
                    maxLength={8}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Color</label>
                  <input
                    className="form-input"
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    style={{ height: '42px', padding: '4px' }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Sort Order</label>
                  <input
                    className="form-input"
                    type="number"
                    value={formData.sortOrder}
                    onChange={(e) => setFormData({ ...formData, sortOrder: e.target.value })}
                    placeholder="1"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button className="btn btn-outline" onClick={handleCancel} disabled={saving}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : editing ? 'Update Track' : 'Create Track'}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="section">
        <div className="section-container">
          {tracks.length > 0 ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: '60px' }}>Order</th>
                  <th>Track</th>
                  <th>Description</th>
                  <th>Courses</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tracks.map((track) => (
                  <tr key={track.id}>
                    <td>{track.sortOrder}</td>
                    <td className="td-title">
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <span aria-hidden="true">{track.icon}</span>
                        <span
                          className="track-badge track-badge-sm"
                          style={{ backgroundColor: track.color, color: '#ffffff' }}
                        >
                          {track.name}
                        </span>
                      </span>
                    </td>
                    <td style={{ maxWidth: '420px' }}>
                      <span
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical' as const,
                          overflow: 'hidden',
                          fontSize: '0.875rem',
                          color: 'var(--gray-600)',
                        }}
                      >
                        {track.description || '—'}
                      </span>
                    </td>
                    <td>{courseCount(track.name)}</td>
                    <td className="td-actions">
                      <button className="btn btn-text" onClick={() => handleEdit(track)}>Edit</button>
                      <button
                        className="btn btn-text btn-danger"
                        onClick={() => handleDelete(track)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="empty-text">No tracks found. Create your first track.</p>
          )}
        </div>
      </section>
    </div>
  );
};

export default ManageTracks;
